import { usePageMeta } from "@/lib/seo";
import { useEffect, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import { CheckCircle2, Clock, Loader2, XCircle, CircleSlash } from "lucide-react";
import { Header } from "@/components/Header";
import { money, StatusBadge } from "@/components/payments/PaymentHistory";
import { api } from "@/lib/api";

const COPY = {
  success: { icon: CheckCircle2, color: "text-emerald-400", title: "Payment successful", body: "Premium is active — enjoy up to 100 replies a day." },
  failed: { icon: XCircle, color: "text-rose-400", title: "Payment failed", body: "Nothing was activated. If money left your account, it will be confirmed automatically or refunded by the provider." },
  pending: { icon: Clock, color: "text-amber-400", title: "Payment pending", body: "Your bank is still confirming the payment. Premium switches on automatically as soon as Razorpay confirms it — no need to pay again." },
  cancelled: { icon: CircleSlash, color: "text-muted-foreground", title: "Payment cancelled", body: "No charge was made. You can try again any time." },
};

export default function PaymentResult() {
  usePageMeta({ title: "Payment" });
  const { status } = useParams();
  const [params] = useSearchParams();
  const order = params.get("order");
  const [pay, setPay] = useState(null);
  const [loading, setLoading] = useState(!!order);
  const c = COPY[status] || COPY.failed;
  const Icon = c.icon;

  useEffect(() => {
    if (!order) return;
    api.get(`/payments/${order}`).then((r) => setPay(r.data)).catch(() => {}).finally(() => setLoading(false));
  }, [order]);

  return (
    <div className="min-h-screen">
      <Header />
      <main className="max-w-lg mx-auto px-4 py-16" data-testid={`payment-${status}-page`}>
        <section className="panel p-8 text-center space-y-4">
          <Icon className={`h-14 w-14 mx-auto ${c.color}`} />
          <h1 className="text-3xl font-extrabold tracking-tight" data-testid="payment-result-title">{c.title}</h1>
          <p className="text-sm text-muted-foreground">{params.get("reason") || c.body}</p>
          {loading && <Loader2 className="h-5 w-5 mx-auto animate-spin text-primary" />}
          {pay && (
            <div className="flex items-center justify-center gap-3 text-sm" data-testid="payment-result-details">
              <span className="font-semibold">{money(pay.amount, pay.currency)}</span><span className="capitalize text-muted-foreground">{pay.provider}</span><StatusBadge status={pay.status} />
            </div>
          )}
          {pay?.transaction_id && <p className="text-xs text-muted-foreground" data-testid="payment-result-txn">Transaction ID: {pay.transaction_id}</p>}
          {pay?.premium_until && pay.status === "paid" && (
            <p className="text-sm font-semibold text-primary" data-testid="payment-result-expiry">Premium active until {new Date(pay.premium_until).toLocaleDateString()}</p>
          )}
          <div className="flex justify-center gap-3 pt-2">
            <Link to="/app" data-testid="payment-result-app-link" className="h-11 px-5 inline-flex items-center rounded-full brand-gradient text-white text-sm font-semibold">Go to generator</Link>
            {status !== "success" && <Link to="/upgrade" data-testid="payment-result-retry-link" className="h-11 px-5 inline-flex items-center rounded-full border border-border text-sm font-semibold">Try again</Link>}
          </div>
        </section>
      </main>
    </div>
  );
}
